<template lang="pug">
yz-filter-menu(:label="$t('panel.doctor')" :is-active="isActive" :display-value="displayValue" :disable-apply="selectedDoctors.length === 0" :loading="loading" :max-height="500" :show-header="false" @update:menu="onMenuOpen" @clear="onClear" @apply="onApply")
  .menu-header.sticky.top-0.z-1
    .font-semibold.text-sm.pa-4 {{ $t('filter_by') }} {{ $t('panel.doctor').toLowerCase() }}
    yz-text-field.text-sm.mx-4.mb-1(v-model="searchText" :placeholder="$t('search')" size="lg" @input="onQueryChanged")
    .d-flex.align-center.justify-end.px-4.py-2(v-if="count > pageSize")
      yz-pagination(v-model="page" :page-size="pageSize" :count="count" :loading="loading" arrow, type="item")

    yz-divider

  .scrollable-content
    yz-skeleton-loader.pa-4(v-if="loading" type="text@10")

    div(v-else-if="error")
      .text-center.py-8
        .text-xs.neutral500--text.mb-3 {{ $t('error_message.error_occured') }}
        yz-btn(size="sm" variant="outlined" color="primary" @click="fetchDoctors") {{ $t('retry') }}

    div(v-else)
      template(v-if="filteredDoctors.length > 0")
        div(v-for="(doctor, index) in filteredDoctors" :key="doctor.id")
          .d-flex.text-sm.px-4.py-3.cursor-pointer.hover-bg(@click="onToggleDoctor(doctor.id)")
            yz-checkbox(:value="selectedDoctors.includes(doctor.id)" @click.native.prevent)
            .ml-4 {{ doctor.name }}
          yz-divider.my-0(v-if="index < filteredDoctors.length - 1")
      template(v-else)
        .text-center.py-8.text-xs.neutral500--text {{ $t('panel.no_items_found') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      doctors: [],
      selectedDoctors: [],
      searchText: '',
      loading: false,
      isActive: false,
      displayValue: '',
      doctorsLoaded: false,
      error: false,
      page: 1,
      pageSize: 10,
      count: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    filteredDoctors() {
      return this.doctors
    }
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.selectedDoctors = []
          this.isActive = false
          this.displayValue = ''
        }
      }
    },
    page() {
      this.fetchDoctors()
    }
  },
  methods: {
    onMenuOpen(isOpen) {
      if (isOpen && !this.doctorsLoaded) {
        this.fetchDoctors()
      }
    },
    onQueryChanged: debounce(function() {
      this.page = 1
      this.fetchDoctors()
    }, 750),
    async fetchDoctors() {
      this.loading = true
      this.error = false
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize
        }
        if (this.searchText) {
          params.search = this.searchText
        }
        const response = await this.$axios.get(`/clinics/${this.company.slug}/providers`, { params })
        this.doctors = response.data.results || response.data || []
        this.count = response.data.count || 0
        this.doctorsLoaded = true
      } catch (error) {
        console.error('Error fetching doctors:', error)
        this.doctors = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
    onToggleDoctor(doctorId) {
      const index = this.selectedDoctors.indexOf(doctorId)
      if (index > -1) {
        this.selectedDoctors.splice(index, 1)
      } else {
        this.selectedDoctors.push(doctorId)
      }
    },
    onClear() {
      this.selectedDoctors = []
      this.searchText = ''
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    onApply() {
      this.isActive = true
      const count = this.selectedDoctors.length

      if (count <= 2) {
        const labels = this.selectedDoctors.map(id => this.doctors.find(d => d.id === id)?.name)
        this.displayValue = labels.join(', ')
      } else {
        this.displayValue = `${count} ${this.$t('selected')}`
      }

      this.$emit('update:filter', {
        key: 'providers',
        value: this.selectedDoctors.join(',')
      })
    },
    reset() {
      this.onClear()
    },
    async restoreFromParams(params) {
      // Restore filter state from URL params
      if (params.providers) {
        // Make sure doctors are loaded first
        if (!this.doctorsLoaded) {
          await this.fetchDoctors()
        }
        // Parse comma-separated IDs
        this.selectedDoctors = params.providers.split(',').map(id => parseInt(id))
        this.onApply()
      }
    }
  }
}
</script>

<style scoped>
.hover-bg:hover {
  background-color: #f5f5f5;
}

.menu-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  overflow: hidden;
}

.scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}
</style>
